export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  actionUrl?: string;
  read: boolean;
  created: string;
  metadata?: Record<string, unknown>;
}

export type NotificationType =
  | 'booking.created'
  | 'booking.updated'
  | 'booking.cancelled'
  | 'booking.deleted'
  | 'availability.requested'
  | 'availability.approved'
  | 'availability.rejected'
  | 'intake.message'
  | 'system.info'
  | 'system.warning'
  | 'product.low_stock'
  | 'promotion.expired';

/** Notification types that users can toggle on/off (only types we actually send) */
export type ToggleableNotificationType =
  | 'booking.created'
  | 'booking.updated'
  | 'booking.cancelled'
  | 'booking.deleted'
  | 'availability.requested'
  | 'availability.approved'
  | 'availability.rejected';

/** Per-user notification preferences — which types to receive */
export type NotificationPreferences = Record<
  ToggleableNotificationType,
  boolean
>;

/** Default notification preferences */
export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  'booking.created': false,
  'booking.updated': true,
  'booking.cancelled': true,
  'booking.deleted': false,
  'availability.requested': true,
  'availability.approved': true,
  'availability.rejected': true,
};

export interface NotificationEvent {
  type: 'notification';
  notification: Notification;
}

export interface SendNotificationRequest {
  userId: string;
  type: NotificationType;
  title: string;
  body: string;
  actionUrl?: string;
  metadata?: Record<string, unknown>;
}

/** Web push subscription — VAPID-based browser push */
export interface WebPushSubscriptionRecord {
  type: 'web';
  deviceId: string;
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
  createdAt: string;
}

/** Native push subscription — FCM token from Capacitor client */
export interface NativePushSubscriptionRecord {
  type: 'native';
  deviceId: string;
  fcmToken: string;
  platform: 'ios' | 'android';
  createdAt: string;
}

/** Tagged union — dedupe by deviceId across web and native */
export type PushSubscriptionRecord =
  | WebPushSubscriptionRecord
  | NativePushSubscriptionRecord;
